import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  SportsTennis as TennisIcon,
  SportsBasketball as BasketballIcon,
  SportsSoccer as FootballIcon,
  SportsVolleyball as VolleyballIcon,
  SportsCricket as CricketIcon,
  SportsHandball as BadmintonIcon,
  Sports as SportsIcon,
} from '@mui/icons-material';
import { generateManualAssessmentForm } from '@utils/generateManualAssessmentForm';

interface Player {
  id: string;
  firstName?: string;
  lastName?: string;
}

interface ManualAssessmentFormsViewProps {
  player: Player;
  selectedSport?: string;
}

const ManualAssessmentFormsView: React.FC<ManualAssessmentFormsViewProps> = ({ player, selectedSport = 'Badminton' }) => {
  const [activeSport, setActiveSport] = React.useState<string>('Badminton');

  React.useEffect(() => {
    setActiveSport(selectedSport || 'Badminton');
  }, [selectedSport]);

  const handleGenerateForm = async (
    type: 'high-level' | 'master-training',
    trainingLevel?: 'beginner' | 'intermediate' | 'advanced' | 'pro'
  ) => {
    await generateManualAssessmentForm(player, selectedSport, type, 'N/A', trainingLevel);
  };

  // Get sport icon
  const getSportIcon = (sport: string) => {
    switch (sport?.toLowerCase()) {
      case 'badminton':
        return <BadmintonIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
      case 'tennis':
        return <TennisIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
      case 'basketball':
        return <BasketballIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
      case 'football':
        return <FootballIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
      case 'volleyball':
        return <VolleyballIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
      case 'cricket':
        return <CricketIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
      default:
        return <SportsIcon sx={{ color: '#1976d2', fontSize: 28 }} />;
    }
  };

  // Use blue download arrow icon for all forms
  const getFormIcon = () => {
    return <FileDownloadIcon sx={{ color: '#2196F3' }} />;
  };

  const forms = [
    {
      id: 'high-level',
      title: 'Skill Assessment - High-level',
      type: 'high-level' as const,
      available: true,
    },
    {
      id: 'beginner',
      title: 'Skill Assessment - Beginner',
      type: 'master-training' as const,
      trainingLevel: 'beginner' as const,
      available: true,
    },
    {
      id: 'intermediate',
      title: 'Skill Assessment - Intermediate',
      type: 'master-training' as const,
      trainingLevel: 'intermediate' as const,
      available: true,
    },
    {
      id: 'advanced',
      title: 'Skill Assessment - Advanced',
      type: 'master-training' as const,
      trainingLevel: 'advanced' as const,
      available: true,
    },
    {
      id: 'pro',
      title: 'Skill Assessment - Pro',
      type: 'master-training' as const,
      trainingLevel: 'pro' as const,
      available: true,
    },
  ];

  return (
    <Box sx={{ pt: 0, mt: 0 }}>
      {/* Sport Name Header - matching Skill Assessment page style */}
      <Box sx={{ mb: 3, mt: 0, pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getSportIcon(selectedSport)}
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600,
              color: '#1976d2',
              fontSize: '1.5rem'
            }}
          >
            {selectedSport}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            - Manual Assessment Forms
          </Typography>
        </Box>
      </Box>

      <Card sx={{ mb: 2, border: '1px solid #dbe8f6' }}>
        <CardContent sx={{ py: '12px !important' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#1976d2' }}>
            Sports
          </Typography>
          <List sx={{ p: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                selected={activeSport.toLowerCase() === 'badminton'}
                onClick={() => setActiveSport('Badminton')}
                sx={{
                  borderRadius: 1,
                  border: '1px solid #e5e7eb',
                  '&.Mui-selected': { bgcolor: '#e3f2fd', borderColor: '#90caf9' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <BadmintonIcon sx={{ color: '#1976d2' }} />
                </ListItemIcon>
                <ListItemText primary="Badminton" />
              </ListItemButton>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Forms List */}
      <List sx={{ bgcolor: 'background.paper', display: activeSport.toLowerCase() === 'badminton' ? 'block' : 'none' }}>
        {forms.map((form) => (
          <ListItem key={form.id} disablePadding>
            <ListItemButton
              onClick={() => form.available ? handleGenerateForm(form.type, form.trainingLevel) : undefined}
              disabled={!form.available}
              sx={{
                py: 2,
                px: 3,
                opacity: form.available ? 1 : 0.5,
                '&:hover': {
                  bgcolor: form.available ? 'action.hover' : 'transparent',
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 48 }}>
                {getFormIcon()}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 500,
                        color: form.available ? 'text.primary' : 'text.disabled',
                      }}
                    >
                      {form.title}
                    </Typography>
                    {!form.available && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.disabled',
                          fontStyle: 'italic',
                        }}
                      >
                        (Coming Soon)
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default ManualAssessmentFormsView;

